import { videoHighlightsService } from './videoHighlightsService';
import { matchDetailsService } from './matchDetails';
import { videosService } from './videosService';
import { supabase } from '../lib/supabase';
import { UploadMetadata } from './socialMediaService';
import { ServiceResult } from './socialMediaService';

interface TemplateVariables {
  matchName: string;
  matchDate: string;
  homeTeam: string;
  awayTeam: string;
  homeScore: number;
  awayScore: number;
  score: string;
  tournament: string;
  highlightType: string;
  highlightName: string;
  duration: number;
  orgName: string;
}

/**
 * Service for generating metadata for social media uploads
 */
class MetadataGeneratorService {
  /**
   * Generate metadata from video/match data
   */
  async generateMetadataForVideo(
    videoId: string,
    platform: 'youtube' | 'instagram' | 'tiktok' | 'facebook' | 'twitter'
  ): Promise<ServiceResult<UploadMetadata>> {
    try {
      // Get video data
      const videoResult = await videosService.getVideoById(videoId);
      if (!videoResult.success || !videoResult.data) {
        return { success: false, error: 'Video not found' };
      }

      const video = videoResult.data;

      // Validate video is ready for upload
      if (video.status !== 'ready') {
        return { success: false, error: 'Video must be transcoded before uploading' };
      }

      if (!video.public_url) {
        return { success: false, error: 'Video transcoded master URL not available' };
      }

      // Get match_id - first try match_videos table, then processing_notes
      let matchId: string | null = null;
      
      // Check match_videos table
      const { data: matchVideos, error: matchVideosError } = await supabase
        .from('match_videos')
        .select('match_id')
        .eq('storage_video_id', videoId)
        .limit(1);

      if (!matchVideosError && matchVideos && matchVideos.length > 0) {
        matchId = matchVideos[0].match_id;
      } else if (video.processing_notes && typeof video.processing_notes === 'object') {
        // Fallback to processing_notes
        matchId = (video.processing_notes as Record<string, any>).match_id || null;
      }

      let variables: TemplateVariables;
      let hasMatch = false;

      // Extract title from filename (remove extension) for fallback
      const fileNameWithoutExt = video.file_name
        ? video.file_name.replace(/\.[^/.]+$/, '')
        : 'Video';

      if (matchId) {
        // Get match data
        const match = await matchDetailsService.getMatchDetails(matchId);
        if (match) {
          hasMatch = true;
          // Build variables from match data (similar to highlights)
          variables = {
            matchName: `${match.home_team?.name || 'Team A'} vs ${match.away_team?.name || 'Team B'}`,
            matchDate: match.start_date
              ? new Date(match.start_date).toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                })
              : new Date().toLocaleDateString(),
            homeTeam: match.home_team?.name || 'Home Team',
            awayTeam: match.away_team?.name || 'Away Team',
            homeScore: match.home_score || 0,
            awayScore: match.away_score || 0,
            score: `${match.home_score || 0}-${match.away_score || 0}`,
            tournament: match.tournament?.name || 'Match',
            highlightType: 'Match Video',
            highlightName: 'Match Video',
            duration: video.duration_seconds || 0,
            orgName: 'Kridah Labs'
          };
        } else {
          // Match not found, use video metadata
          hasMatch = false;
          variables = {
            matchName: fileNameWithoutExt,
            matchDate: video.created_at
              ? new Date(video.created_at).toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                })
              : new Date().toLocaleDateString(),
            homeTeam: 'Team A',
            awayTeam: 'Team B',
            homeScore: 0,
            awayScore: 0,
            score: '0-0',
            tournament: 'Match',
            highlightType: 'Video',
            highlightName: fileNameWithoutExt,
            duration: video.duration_seconds || 0,
            orgName: 'Kridah Labs'
          };
        }
      } else {
        // No matchId, use video metadata
        hasMatch = false;
        variables = {
          matchName: fileNameWithoutExt,
          matchDate: video.created_at
            ? new Date(video.created_at).toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'long',
                day: 'numeric'
              })
            : new Date().toLocaleDateString(),
          homeTeam: 'Team A',
          awayTeam: 'Team B',
          homeScore: 0,
          awayScore: 0,
          score: '0-0',
          tournament: 'Match',
          highlightType: 'Video',
          highlightName: fileNameWithoutExt,
          duration: video.duration_seconds || 0,
          orgName: 'Kridah Labs'
        };
      }

      // Generate platform-specific metadata
      const metadata = this.generatePlatformMetadataForVideo(platform, variables, video, hasMatch);

      return { success: true, data: metadata };
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      console.error('generateMetadataForVideo failed:', errorMessage);
      return { success: false, error: errorMessage };
    }
  }

  /**
   * Generate platform-specific metadata for videos
   */
  private generatePlatformMetadataForVideo(
    platform: string,
    variables: TemplateVariables,
    video: any,
    hasMatch: boolean
  ): UploadMetadata {
    const videoTitle = video.file_name
      ? video.file_name.replace(/\.[^/.]+$/, '')
      : 'Video';
    const videoDescription = video.description || '';

    switch (platform) {
      case 'youtube':
        if (hasMatch) {
          return {
            title: `${variables.matchName} - ${variables.tournament} Match Video | ${variables.matchDate}`,
            description: this.generateYouTubeDescription(variables),
            tags: this.generateYouTubeTags(variables),
            privacyStatus: 'public',
            categoryId: '17', // Sports
            isShort: false
          };
        } else {
          return {
            title: videoTitle,
            description: videoDescription || `Watch ${videoTitle}`,
            tags: this.generateVideoTagsFromFilename(videoTitle),
            privacyStatus: 'public',
            categoryId: '17', // Sports
            isShort: false
          };
        }

      case 'instagram':
        if (hasMatch) {
          return {
            title: `${variables.matchName} Match Video`,
            description: this.generateInstagramDescription(variables),
            tags: this.generateInstagramTags(variables),
            privacyStatus: 'public'
          };
        } else {
          return {
            title: videoTitle,
            description: videoDescription || `⚽ ${videoTitle}`,
            tags: this.generateVideoTagsFromFilename(videoTitle),
            privacyStatus: 'public'
          };
        }

      case 'tiktok':
        if (hasMatch) {
          return {
            title: `${variables.matchName} Match Video`,
            description: this.generateTikTokDescription(variables),
            tags: this.generateTikTokTags(variables),
            privacyStatus: 'public'
          };
        } else {
          return {
            title: videoTitle,
            description: videoDescription || `⚽ ${videoTitle}`,
            tags: this.generateVideoTagsFromFilename(videoTitle),
            privacyStatus: 'public'
          };
        }

      case 'facebook':
        if (hasMatch) {
          return {
            title: `${variables.matchName} - ${variables.tournament} Match Video`,
            description: this.generateFacebookDescription(variables),
            tags: this.generateFacebookTags(variables),
            privacyStatus: 'public'
          };
        } else {
          return {
            title: videoTitle,
            description: videoDescription || `Watch ${videoTitle}`,
            tags: this.generateVideoTagsFromFilename(videoTitle),
            privacyStatus: 'public'
          };
        }

      case 'twitter':
        if (hasMatch) {
          return {
            title: `${variables.matchName} Match Video`,
            description: this.generateTwitterDescription(variables),
            tags: this.generateTwitterTags(variables),
            privacyStatus: 'public'
          };
        } else {
          return {
            title: videoTitle,
            description: videoDescription || `⚽ ${videoTitle}`,
            tags: this.generateVideoTagsFromFilename(videoTitle),
            privacyStatus: 'public'
          };
        }

      default:
        return {
          title: hasMatch ? `${variables.matchName} Match Video` : videoTitle,
          description: hasMatch
            ? `Watch match video from ${variables.matchName}`
            : videoDescription || `Watch ${videoTitle}`,
          tags: hasMatch ? ['football', 'match', 'video'] : this.generateVideoTagsFromFilename(videoTitle),
          privacyStatus: 'public'
        };
    }
  }

  /**
   * Generate tags from video filename
   */
  private generateVideoTagsFromFilename(filename: string): string[] {
    const tags = ['football', 'video', 'soccer', 'match'];
    
    // Extract keywords from filename (remove common words, extract meaningful parts)
    const words = filename
      .toLowerCase()
      .replace(/[^a-z0-9\s]/g, ' ')
      .split(/\s+/)
      .filter(word => word.length > 2 && !['the', 'and', 'for', 'with', 'from'].includes(word))
      .slice(0, 5); // Limit to 5 keywords
    
    tags.push(...words);
    
    return Array.from(new Set(tags.filter(Boolean)));
  }

  /**
   * Generate metadata from highlight/match data
   */
  async generateMetadata(
    highlightId: string,
    platform: 'youtube' | 'instagram' | 'tiktok' | 'facebook' | 'twitter'
  ): Promise<ServiceResult<UploadMetadata>> {
    try {
      // Get highlight data
      const highlightResult = await videoHighlightsService.getHighlightById(highlightId);
      if (!highlightResult.success || !highlightResult.data) {
        return { success: false, error: 'Highlight not found' };
      }

      const highlight = highlightResult.data;

      // Get match data
      const match = await matchDetailsService.getMatchDetails(highlight.match_id);
      if (!match) {
        return { success: false, error: 'Match not found' };
      }

      // Calculate duration from segments if available
      let duration = 0;
      if (highlight.segments_data && Array.isArray(highlight.segments_data)) {
        duration = highlight.segments_data.reduce((total, segment) => {
          const segmentDuration = (segment.endTime || 0) - (segment.startTime || 0);
          return total + segmentDuration;
        }, 0);
      }

      // Build variables
      const variables: TemplateVariables = {
        matchName: `${match.home_team?.name || 'Team A'} vs ${match.away_team?.name || 'Team B'}`,
        matchDate: match.start_date
          ? new Date(match.start_date).toLocaleDateString('en-US', {
              year: 'numeric',
              month: 'long',
              day: 'numeric'
            })
          : new Date().toLocaleDateString(),
        homeTeam: match.home_team?.name || 'Home Team',
        awayTeam: match.away_team?.name || 'Away Team',
        homeScore: match.home_score || 0,
        awayScore: match.away_score || 0,
        score: `${match.home_score || 0}-${match.away_score || 0}`,
        tournament: match.tournament?.name || 'Match',
        highlightType: highlight.name || 'Highlights',
        highlightName: highlight.name,
        duration: duration,
        orgName: 'Kridah Labs' // Or get from org context
      };

      // Generate platform-specific metadata
      const metadata = this.generatePlatformMetadata(platform, variables);

      return { success: true, data: metadata };
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      console.error('generateMetadata failed:', errorMessage);
      return { success: false, error: errorMessage };
    }
  }

  /**
   * Generate platform-specific metadata
   */
  private generatePlatformMetadata(
    platform: string,
    variables: TemplateVariables
  ): UploadMetadata {
    switch (platform) {
      case 'youtube':
        return {
          title: `${variables.matchName} - ${variables.tournament} Highlights | ${variables.matchDate}`,
          description: this.generateYouTubeDescription(variables),
          tags: this.generateYouTubeTags(variables),
          privacyStatus: 'public',
          categoryId: '17', // Sports
          isShort: false // Default to regular video
        };

      // Future platforms
      case 'instagram':
        return {
          title: `${variables.matchName} Highlights`,
          description: this.generateInstagramDescription(variables),
          tags: this.generateInstagramTags(variables),
          privacyStatus: 'public'
        };

      case 'tiktok':
        return {
          title: `${variables.matchName} Highlights`,
          description: this.generateTikTokDescription(variables),
          tags: this.generateTikTokTags(variables),
          privacyStatus: 'public'
        };

      case 'facebook':
        return {
          title: `${variables.matchName} - ${variables.tournament} Highlights`,
          description: this.generateFacebookDescription(variables),
          tags: this.generateFacebookTags(variables),
          privacyStatus: 'public'
        };

      case 'twitter':
        return {
          title: `${variables.matchName} Highlights`,
          description: this.generateTwitterDescription(variables),
          tags: this.generateTwitterTags(variables),
          privacyStatus: 'public'
        };

      default:
        return {
          title: `${variables.matchName} Highlights`,
          description: `Watch highlights from ${variables.matchName}`,
          tags: ['football', 'highlights'],
          privacyStatus: 'public'
        };
    }
  }

  /**
   * Generate YouTube description
   */
  private generateYouTubeDescription(variables: TemplateVariables): string {
    return (
      `Watch the best moments from ${variables.matchName}.\n\n` +
      `Final Score: ${variables.score}\n\n` +
      `${variables.tournament}\n\n` +
      `#Football #Highlights #${variables.homeTeam.replace(/\s+/g, '')} #${variables.awayTeam.replace(/\s+/g, '')}`
    );
  }

  /**
   * Generate YouTube tags
   */
  private generateYouTubeTags(variables: TemplateVariables): string[] {
    const tags = [
      'football',
      'highlights',
      'soccer',
      variables.homeTeam,
      variables.awayTeam,
      variables.tournament,
      'match highlights',
      'goals'
    ];

    // Remove duplicates and empty values
    return Array.from(new Set(tags.filter(Boolean)));
  }

  /**
   * Generate Instagram description
   */
  private generateInstagramDescription(variables: TemplateVariables): string {
    return (
      `⚽ ${variables.matchName}\n\n` +
      `Final Score: ${variables.score}\n\n` +
      `${variables.tournament}\n\n` +
      `#Football #Highlights #${variables.homeTeam.replace(/\s+/g, '')} #${variables.awayTeam.replace(/\s+/g, '')}`
    );
  }

  /**
   * Generate Instagram tags
   */
  private generateInstagramTags(variables: TemplateVariables): string[] {
    return [
      'football',
      'highlights',
      'soccer',
      variables.homeTeam.replace(/\s+/g, ''),
      variables.awayTeam.replace(/\s+/g, ''),
      variables.tournament.replace(/\s+/g, '')
    ].filter(Boolean);
  }

  /**
   * Generate TikTok description
   */
  private generateTikTokDescription(variables: TemplateVariables): string {
    return (
      `⚽ ${variables.matchName} - Final Score: ${variables.score}\n\n` +
      `#Football #Highlights #${variables.homeTeam.replace(/\s+/g, '')} #${variables.awayTeam.replace(/\s+/g, '')}`
    );
  }

  /**
   * Generate TikTok tags
   */
  private generateTikTokTags(variables: TemplateVariables): string[] {
    return [
      'football',
      'highlights',
      'soccer',
      variables.homeTeam.replace(/\s+/g, ''),
      variables.awayTeam.replace(/\s+/g, '')
    ].filter(Boolean);
  }

  /**
   * Generate Facebook description
   */
  private generateFacebookDescription(variables: TemplateVariables): string {
    return (
      `Watch the best moments from ${variables.matchName}.\n\n` +
      `Final Score: ${variables.score}\n\n` +
      `${variables.tournament}`
    );
  }

  /**
   * Generate Facebook tags
   */
  private generateFacebookTags(variables: TemplateVariables): string[] {
    return [
      'football',
      'highlights',
      'soccer',
      variables.homeTeam,
      variables.awayTeam,
      variables.tournament
    ].filter(Boolean);
  }

  /**
   * Generate Twitter description
   */
  private generateTwitterDescription(variables: TemplateVariables): string {
    return `⚽ ${variables.matchName} Highlights\n\nFinal Score: ${variables.score}\n\n${variables.tournament}`;
  }

  /**
   * Generate Twitter tags
   */
  private generateTwitterTags(variables: TemplateVariables): string[] {
    return [
      'football',
      'highlights',
      'soccer',
      variables.homeTeam.replace(/\s+/g, ''),
      variables.awayTeam.replace(/\s+/g, '')
    ].filter(Boolean);
  }
}

export const metadataGeneratorService = new MetadataGeneratorService();

